/* eslint-disable camelcase */
import { useEffect, useState } from 'react';

import { Text } from '@gympass/yoga';
import { FormattedMessage, useIntl } from 'react-intl';
import { formatDateHour } from 'utils';

import useSessionStatusMap from 'hooks/useSessionStatusMap';
import { ActionType } from 'services/specialist';

import { TableColumn } from 'components/Table/types';
import { TableLoading } from 'components/TableLoading';
import MignifyingGlass from 'images/mignifying-glass.svg';
import { SessionRecord } from 'pages/SessionHistory/types';

import { SessionsHistoyTableProps } from './types';

import * as S from './styles';

import { Tooltip } from '..';

const SessionsHistoryTable: React.FC<SessionsHistoyTableProps> = ({
  data,
  loading,
  currentOrdering,
  orderData,
}) => {
  const intl = useIntl();
  const sessionStatusMap = useSessionStatusMap();
  const [innerData, setInnerData] = useState<SessionRecord[]>(data);

  const LOADING_LINES_NUMBER = 9;

  const handleOrderArrows = (column: string) => {
    if (currentOrdering && orderData) {
      const handleArrowsPosition = () => {
        if (
          Object.keys(currentOrdering).length !== 0 &&
          currentOrdering.column === column
        ) {
          return currentOrdering.direction === 'asc' ? (
            <S.AscOrdered data-testid={`arrowAscOrdered-${column}`} />
          ) : (
            <S.DescOrdered data-testid={`arrowDescOrdered-${column}`} />
          );
        }

        return <S.NonOrdered data-testid={`arrowNonOrdered-${column}`} />;
      };

      return (
        <S.OrderButton
          data-testid={`orderButton-${column}`}
          onClick={() => orderData(column)}
        >
          {handleArrowsPosition()}
        </S.OrderButton>
      );
    }

    return <></>;
  };

  const handleTags = (status: string, sessionData: SessionRecord) => {
    const { initialSession, actionType, inviteStatus } = sessionData;
    let tagsCount = 0;
    const extraTags: string[] = [];

    const addTag = (condition: boolean, label: string) => {
      if (condition) {
        tagsCount += 1;
        extraTags.push(label);
      }
    };

    if (initialSession) {
      if (!actionType) {
        extraTags.push('SESSION.HISTORY.CP.TAG.WELCOME');
      } else {
        addTag(true, 'SESSION.HISTORY.CP.TAG.WELCOME');
      }
    }

    if (inviteStatus) {
      const inviteLabel = inviteStatus === 'accepted' ? 'ACCEPTED' : 'SENT';
      addTag(!!actionType, `CPPLATFORM.TAGNEWTIME.${inviteLabel}`);
      if (!actionType) {
        extraTags.push(`CPPLATFORM.TAGNEWTIME.${inviteLabel}`);
      }
    }

    return (
      <S.StatusContainer>
        {sessionStatusMap(status)}
        <S.TagContainer data-testid="tag-container">
          {(actionType === ActionType.CREATED ||
            actionType === ActionType.UPDATED) && (
            <S.ActionTag
              variant="success"
              mr="small"
              data-testid="tag-action-edited"
            >
              <FormattedMessage
                id={`CPPLATFORM.LABEL.${
                  actionType === ActionType.CREATED ? 'CREATED' : 'EDITED'
                }`}
              />
            </S.ActionTag>
          )}
          {tagsCount > 0 && (
            <Tooltip description={extraTags}>
              <S.InitialSessionTag mr="small" data-testid="tag-action-extra">
                + {tagsCount}
              </S.InitialSessionTag>
            </Tooltip>
          )}
          {tagsCount === 0 && extraTags.length === 1 && (
            <S.InitialSessionTag mr="small" data-testid="tag-action-extra">
              <FormattedMessage id={extraTags[0]} />
            </S.InitialSessionTag>
          )}
        </S.TagContainer>
      </S.StatusContainer>
    );
  };

  const SessionsHistoryColumns: TableColumn[] = [
    {
      dataKey: 'startAt',
      key: 'session_startAt',
      title: intl.formatMessage({ id: 'SESSION_HISTORY.TABLE.TITLE.DAY_HOUR' }),
      render: (startAt: string) =>
        formatDateHour(startAt, { locale: intl.locale }),
      titleRender: title => {
        return (
          <S.CentrilizedContainer>
            <S.TableHeaderText
              isOrdered={currentOrdering?.column === 'start_at'}
            >
              {title}
            </S.TableHeaderText>
            {handleOrderArrows('start_at')}
          </S.CentrilizedContainer>
        );
      },
    },
    {
      dataKey: 'userFullName',
      key: 'session_fullName',
      title: intl.formatMessage({
        id: 'SESSION.HISTORY.CP.TITLE.NAME',
      }),
    },
    {
      dataKey: 'status',
      key: 'session_status',
      title: intl.formatMessage({ id: 'SESSION_HISTORY.TABLE.TITLE.STATUS' }),
      titleRender: title => {
        return (
          <S.CentrilizedContainer>
            <S.TableHeaderText isOrdered={currentOrdering?.column === 'status'}>
              {title}
            </S.TableHeaderText>
            {handleOrderArrows('status')}
          </S.CentrilizedContainer>
        );
      },
      render: handleTags,
    },
  ];

  useEffect(() => {
    if (loading) {
      setInnerData([]);
    } else {
      setInnerData(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  return (
    <S.Container>
      <S.StyledTable columns={SessionsHistoryColumns} data={innerData} />

      {loading && <TableLoading linesNumber={LOADING_LINES_NUMBER} />}
      {!loading && !innerData.length && (
        <S.NoDataContainer data-testid="noDataContainer">
          <MignifyingGlass />
          <S.NoDataContent>
            <S.NoDataTitle data-testid="noDataTitle">
              <FormattedMessage id="SESSION_HISTORY.TABLE.NO_DATA.TITLE" />
            </S.NoDataTitle>
            <Text.Small data-testid="noDataContent">
              <FormattedMessage id="SESSION_HISTORY.TABLE.NO_DATA.CONTENT" />
            </Text.Small>
          </S.NoDataContent>
        </S.NoDataContainer>
      )}
    </S.Container>
  );
};

export default SessionsHistoryTable;
